import {useState, useEffect} from 'react';
import NetworkGraph from '../components/NetworkGraph';
import type {ProfileSummary, FriendData, SteamID64, SteamPlayer, BanInfo, Minutes} from '../types/steam';
import {
    getPlayerSummary,
    getPlayerBans,
    getFriendsList,
    getPlayersBatch,
    getBansBatch,
    getCS2Playtime,
    getRecentNames,
} from '../utils/steamApi';
import {SteamID64 as toSteamID64, BanCount as toBanCount, Days as toDays} from '../types/steam';
import {getFriends as getCachedFriends, setFriend as setCachedFriend} from '../utils/cache';
import {normalizeRecentNames} from '../utils/dateUtils';

/**
 * Utility function to add a delay between iterations
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate a scaled delay based on the number of items to iterate over.
 * Smaller lists get longer delays per item for better visibility.
 * Larger lists get shorter delays per item to keep total time reasonable.
 * 
 * Target timings:
 * - 10 items: ~100ms each (total ~1s)
 * - 100 items: ~100ms each (total ~10s)
 * - 200+ items: ~50ms each (total ~10s+)
 * 
 * @param count - Total number of items to iterate over
 * @returns Delay in milliseconds to use per iteration
 */
const calculateScaledDelay = (count: number): number => {
    if (count <= 0) return 0;
    
    // For small counts (1-10), use 200ms per item
    if (count <= 10) return 200;
    
    // For medium counts (11-50), keep at 200ms per item
    if (count <= 30) return 200;
    
    // For large counts (100+), scale down to keep total time around 10 seconds
    // This gives ~50ms per item for 200 items
    return Math.max(50, Math.floor(10000 / count));
};

function NetworkPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [loadingStatus, setLoadingStatus] = useState('Initializing...');
    const [error, setError] = useState<string | null>(null);
    const [profileSummary, setProfileSummary] = useState<ProfileSummary | null>(null);
    const [friends, setFriends] = useState<FriendData[]>([]);
    const [friendsOfFriends, setFriendsOfFriends] = useState<Map<SteamID64, FriendData[]>>(new Map());
    const [friendCounts, setFriendCounts] = useState<Map<SteamID64, number>>(new Map());

    useEffect(() => {
        const loadNetworkData = async () => {
            try {
                // Get Steam ID from URL path
                const pathMatch = window.location.pathname.match(/^\/network\/([^/]+)$/);
                if (!pathMatch) {
                    setError('Invalid URL. Please navigate from a profile page.');
                    setIsLoading(false);
                    return;
                }

                const steamId = toSteamID64(pathMatch[1]);
                
                setLoadingStatus('Loading root profile...');

                // Check cache for main profile first
                const cachedProfile = getCachedFriends([steamId]).cached[steamId];
                let player: SteamPlayer;
                let bans: BanInfo;
                let playtime: Minutes | undefined;
                let recentNames: string[];

                if (cachedProfile) {
                    // Use cached data
                    player = cachedProfile.player;
                    bans = cachedProfile.bans;
                    [playtime, recentNames] = await Promise.all([
                        getCS2Playtime(steamId),
                        getRecentNames(steamId),
                    ]);
                } else {
                    // Load from API and cache
                    [player, bans, playtime, recentNames] = await Promise.all([
                        getPlayerSummary(steamId),
                        getPlayerBans(steamId),
                        getCS2Playtime(steamId),
                        getRecentNames(steamId),
                    ]);
                    
                    // Cache the main profile
                    setCachedFriend(steamId, {
                        player,
                        bans,
                    });
                }

                const summary: ProfileSummary = {
                    id64: steamId,
                    persona: player.personaname,
                    profileUrl: player.profileurl,
                    recentNames: normalizeRecentNames(recentNames, player.personaname),
                    vacBanned: bans.VACBanned,
                    vacBans: bans.NumberOfVACBans,
                    daysSinceLastBan: bans.DaysSinceLastBan,
                    gameBans: bans.NumberOfGameBans,
                    timeCreated: player.timecreated,
                    cs2PlaytimeMinutes: playtime,
                    friendCount: 0,
                    friendVacCount: toBanCount(0),
                    friendGameCount: toBanCount(0),
                };

                setProfileSummary(summary);
                setIsLoading(false); // Show graph with root node only
                
                setLoadingStatus('Loading friends list...');

                // Load friends list
                const friendsList = await getFriendsList(steamId);
                const friendIds = friendsList.map(f => f.steamid);

                // Store root's friend count
                const counts = new Map<SteamID64, number>();
                counts.set(steamId, friendIds.length);
                setFriendCounts(new Map(counts));

                // Use cache to get friends data
                const {cached, missing} = getCachedFriends(friendIds);
                
                setLoadingStatus(`Loading friends (${Object.keys(cached).length}/${friendIds.length} from cache)...`);

                const friendsData: FriendData[] = [];
                
                // Calculate scaled delay based on total friend count
                const friendDelay = calculateScaledDelay(friendIds.length);
                
                // Add cached friends one at a time with delay for visual effect
                for (const data of Object.values(cached)) {
                    friendsData.push(data);
                    setFriends([...friendsData]);
                    setLoadingStatus(`Loading friends (${friendsData.length}/${friendIds.length})...`);
                    await delay(friendDelay);
                }

                // Batch load all missing friends data, then display one at a time
                if (missing.length > 0) {
                    setLoadingStatus(`Loading ${missing.length} friends...`);
                    
                    const [friendPlayers, friendBans] = await Promise.all([
                        getPlayersBatch(missing), getBansBatch(missing),
                    ]);

                    // Process and display each friend one at a time with delay
                    for (const player of friendPlayers) {
                        const banInfo = friendBans.find(b => b.SteamId === player.steamid);
                        const friendData: FriendData = {
                            player, bans: banInfo || {
                                SteamId: player.steamid,
                                CommunityBanned: false,
                                VACBanned: false,
                                NumberOfVACBans: toBanCount(0),
                                DaysSinceLastBan: toDays(0),
                                NumberOfGameBans: toBanCount(0),
                                EconomyBan: 'none',
                            },
                        };
                        friendsData.push(friendData);
                        setCachedFriend(player.steamid, friendData);
                        
                        // Update friends state after each friend for iterative display
                        setFriends([...friendsData]);
                        setLoadingStatus(`Loading friends (${friendsData.length}/${friendIds.length})...`);
                        await delay(friendDelay);
                    }
                }

                // Ensure all friends are rendered before loading friends-of-friends
                // by adding a small delay after completing friend loading
                setLoadingStatus('All friends loaded, preparing to load friend networks...');
                await delay(500);

                setLoadingStatus('Loading friends of friends...');

                // Load friends of friends one at a time per friend (breadth-first)
                const fofMap = new Map<SteamID64, FriendData[]>();
                let processedCount = 0;
                
                // Use smaller delay for friends of friends (25ms)
                const fofDelay = 25;
                
                for (const friendId of friendIds) {
                    processedCount++;
                    setLoadingStatus(`Loading friend's network ${processedCount}/${friendIds.length}...`);
                    
                    try {
                        // Load this friend's friend list
                        const fofList = await getFriendsList(friendId);
                        const fofIds = fofList.map(f => f.steamid);
                        
                        // Store friend count for this friend
                        counts.set(friendId, fofIds.length);
                        
                        if (fofIds.length > 0) {
                            // Use cache for friends of friends
                            const {cached: fofCached, missing: fofMissing} = getCachedFriends(fofIds);
                            const fofData: FriendData[] = Object.values(fofCached);
                            
                            // Batch load all missing FoF data for this friend
                            if (fofMissing.length > 0) {
                                const [fofPlayers, fofBans] = await Promise.all([
                                    getPlayersBatch(fofMissing), getBansBatch(fofMissing),
                                ]);
                                
                                // Process each friend-of-friend
                                for (const player of fofPlayers) {
                                    const banInfo = fofBans.find(b => b.SteamId === player.steamid);
                                    const fofFriendData: FriendData = {
                                        player, bans: banInfo || {
                                            SteamId: player.steamid,
                                            CommunityBanned: false,
                                            VACBanned: false,
                                            NumberOfVACBans: toBanCount(0),
                                            DaysSinceLastBan: toDays(0),
                                            NumberOfGameBans: toBanCount(0),
                                            EconomyBan: 'none',
                                        },
                                    };
                                    fofData.push(fofFriendData);
                                    setCachedFriend(player.steamid, fofFriendData);
                                }
                            }
                            
                            // Add to map
                            fofMap.set(friendId, fofData);
                            
                            // Update state to trigger graph update after each friend's network with smaller delay
                            setFriendsOfFriends(new Map(fofMap));
                            setFriendCounts(new Map(counts));
                            await delay(fofDelay);
                        }
                    } catch (err) {
                        console.warn(`Failed to load friends of friend ${friendId}:`, err);
                        // Continue with next friend
                    }
                }

                const allIds = new Set<SteamID64>([
                    ...fofMap.keys(), ...Array.from(fofMap.values())
                        .flatMap(friendArray => friendArray.map(friend => friend.player.steamid))
                ]);

                console.log("Friends of friends loaded for network page: ", allIds.size);
                setLoadingStatus('Network loaded!');
            } catch (err) {
                console.error('Failed to load network data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load network data');
                setIsLoading(false);
            }
        };

        loadNetworkData();
    }, []);

    const handleClose = () => {
        // Navigate back to the profile page
        if (profileSummary) {
            window.location.href = `/${profileSummary.id64}`;
        } else {
            window.location.href = '/';
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 dark:bg-gray-950">
                <div className="card p-8">
                    <div className="flex flex-col items-center gap-4">
                        <div className="spinner"></div>
                        <p className="text-lg">{loadingStatus}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 dark:bg-gray-950">
                <div className="card p-8 max-w-md">
                    <h2 className="text-xl font-semibold mb-4 text-red-600 dark:text-red-400">Error</h2>
                    <p className="mb-4">{error}</p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="button"
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    if (!profileSummary || friends.length === 0) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 dark:bg-gray-950">
                <div className="card p-8 max-w-md">
                    <h2 className="text-xl font-semibold mb-4">No Data</h2>
                    <p className="mb-4">No friends data available for this profile.</p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="button"
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <NetworkGraph
                mainProfile={profileSummary}
                friends={friends}
                friendsOfFriends={friendsOfFriends}
                friendCounts={friendCounts}
                onClose={handleClose}
            />
            {loadingStatus !== 'Network loaded!' && (
                <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 card p-3 text-sm flex items-center gap-3">
                    <div className="spinner" style={{width: '20px', height: '20px', borderWidth: '2px'}}></div>
                    <span>{loadingStatus}</span>
                </div>
            )}
        </>
    );
}

export default NetworkPage;
